import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Printer } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDate, formatDateTime } from '@/lib/date'
import { lineTotal, money, priceRowLabel } from '@/lib/money'
import { specEntries, useOptionGroups } from '@/lib/options'
import { SELF_PICKUP } from '@/lib/pickupHours'
import type { InvoiceSeller, Order } from '@/types'
import Button from '@/components/ui/Button'
import LogoLoader from '@/components/ui/LogoLoader'
import { paymentMethodLabels } from '@/components/order/PaymentBadge'

/** The word stamped beside the invoice number. A cancelled order says so whatever the money did. */
function stamp(order: Order): string {
  if (order.status === 'cancelled') return 'CANCELLED'
  switch (order.payment_status) {
    case 'paid':
      return 'PAID'
    case 'refunded':
      return 'REFUNDED'
    case 'refund_due':
      return 'REFUND DUE'
    default:
      return 'UNPAID'
  }
}

/** A small uppercase heading over each block of the invoice. */
function Heading({ children }: { children: ReactNode }) {
  return <p className="mb-1.5 text-2xs font-bold uppercase tracking-wider text-muted">{children}</p>
}

/**
 * The customer's invoice for one order, printed from the browser (Save as PDF
 * to download), the way the job sheet is. One page for the admin and sales
 * consoles and the customer's own account: `basePath` picks the endpoint, ''
 * being the customer's. The invoice number is the order number.
 *
 * It is a customer document whoever prints it: the price is what was charged,
 * never the team's "agreed price (system RM x)" note, and the remark is left
 * off as on the customer's order page.
 *
 * print-color-adjust keeps the shaded table header and total box on paper;
 * browsers drop backgrounds when printing otherwise.
 */
export default function InvoicePage({ basePath }: { basePath: '/admin' | '/sales' | '' }) {
  const { id } = useParams()
  const optionGroups = useOptionGroups()

  const { data, isLoading } = useQuery({
    queryKey: ['invoice', basePath, id],
    queryFn: async () => (await api.get<{ data: Order; seller: InvoiceSeller }>(`${basePath}/orders/${id}/invoice`)).data,
  })

  if (isLoading || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LogoLoader tone="dark" />
      </div>
    )
  }

  const order = data.data
  const seller = data.seller
  const customer = order.agent
  const address = order.delivery_address
  const pickup = order.delivery_method === SELF_PICKUP
  const paid = ['paid', 'refunded', 'refund_due'].includes(order.payment_status ?? '')
  const cancelled = order.status === 'cancelled'
  const showBank = !paid && !cancelled && Boolean(seller.bank_name || seller.bank_account_no)

  const meta: [string, string][] = [
    ['Invoice no', order.number],
    ['Date', formatDate(order.created_at)],
    ...(order.job ? [['Job', order.job.code] as [string, string]] : []),
  ]

  return (
    <div className="min-h-screen bg-app p-4 sm:p-6 print:bg-white print:p-0">
      <div className="no-print mx-auto mb-4 flex max-w-4xl flex-wrap items-center justify-between gap-2">
        <Link
          to={`${basePath || '/account'}/orders/${id}`}
          className="inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:underline"
        >
          <ArrowLeft className="size-4" /> Back to order
        </Link>
        <Button onClick={() => window.print()} title="Choose Save as PDF in the print dialog to download it">
          <Printer className="size-4" /> Download / Print
        </Button>
      </div>

      <div className="mx-auto max-w-4xl rounded-card border border-line bg-white p-5 text-sm text-ink [-webkit-print-color-adjust:exact] [print-color-adjust:exact] sm:p-10 print:rounded-none print:border-0 print:p-0">
        {/* Seller left, invoice title and details right */}
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between print:flex-row print:items-start print:justify-between">
          <div className="min-w-0 space-y-0.5">
            <img src="/logo_hd.png" alt={seller.business_name} className="mb-3 h-11 w-auto" />
            <p className="text-base font-extrabold text-navy">{seller.business_name}</p>
            {(seller.registration_no || seller.sst_no) && (
              <p className="text-xs text-muted">
                {[seller.registration_no && `Reg. no. ${seller.registration_no}`, seller.sst_no && `SST no. ${seller.sst_no}`]
                  .filter(Boolean)
                  .join('  |  ')}
              </p>
            )}
            {seller.address && <p className="whitespace-pre-line text-xs leading-relaxed text-muted">{seller.address}</p>}
            {(seller.phone || seller.email) && (
              <p className="text-xs text-muted">{[seller.phone, seller.email].filter(Boolean).join('  |  ')}</p>
            )}
          </div>

          <div className="shrink-0 sm:text-right print:text-right">
            <p className="text-3xl font-extrabold tracking-[0.2em] text-navy">INVOICE</p>
            <dl className="mt-3 grid grid-cols-[auto_auto] justify-start gap-x-5 gap-y-1 text-sm sm:justify-end print:justify-end">
              {meta.map(([label, value]) => (
                <div key={label} className="contents">
                  <dt className="text-muted sm:text-right print:text-right">{label}</dt>
                  <dd className="font-semibold sm:text-right print:text-right">{value}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 inline-block rounded border-2 border-navy px-3 py-1 text-xs font-extrabold tracking-[0.2em] text-navy">
              {stamp(order)}
            </p>
          </div>
        </div>

        <div className="mt-6 border-t-2 border-navy" />

        {/* Who, where, and how it was paid */}
        <div className="mt-6 grid divide-y divide-line rounded-lg border border-line bg-foreground/[0.03] sm:grid-cols-3 sm:divide-x sm:divide-y-0 print:grid-cols-3 print:divide-x print:divide-y-0">
          <div className="p-4">
            <Heading>Bill to</Heading>
            <p className="font-semibold">{customer?.company_name || customer?.name || '-'}</p>
            {customer?.company_name && customer.name && <p>{customer.name}</p>}
            {customer?.phone && <p className="text-xs text-muted">{customer.phone}</p>}
            {customer?.email && <p className="break-all text-xs text-muted">{customer.email}</p>}
            {order.customer_ref && <p className="mt-1.5 text-xs">Your ref: {order.customer_ref}</p>}
          </div>

          <div className="p-4">
            <Heading>{pickup ? 'Collection' : 'Deliver to'}</Heading>
            {address ? (
              <>
                <p className="font-semibold">{address.recipient_name}</p>
                <p className="text-xs leading-relaxed">
                  {[address.line1, address.line2].filter(Boolean).join(', ')}
                  <br />
                  {address.postcode} {address.city}, {address.state}
                </p>
                <p className="text-xs text-muted">{address.phone}</p>
              </>
            ) : (
              <p className="font-semibold">{pickup ? 'Self pickup' : (order.delivery_method ?? '-')}</p>
            )}
            {address && order.delivery_method && <p className="text-xs text-muted">By {order.delivery_method}</p>}
            {order.expected_completion_date && (
              <p className="mt-1.5 text-xs">Delivery date: {formatDate(order.expected_completion_date)}</p>
            )}
          </div>

          <div className="p-4">
            <Heading>Payment</Heading>
            <p className="font-semibold">{stamp(order).charAt(0) + stamp(order).slice(1).toLowerCase()}</p>
            {paid && order.payment_method && <p>{paymentMethodLabels[order.payment_method]}</p>}
            {paid && order.paid_at && <p className="text-xs text-muted">{formatDateTime(order.paid_at)}</p>}
            {paid && order.payment_reference && <p className="break-all text-xs text-muted">Ref: {order.payment_reference}</p>}
            {!paid && !cancelled && <p className="text-xs">Amount due: <span className="font-semibold">{money(order.total)}</span></p>}
            {order.payment_status === 'refunded' && (
              <p className="text-xs text-muted">
                Refunded {money(order.refunded_amount)}
                {order.refunded_at ? ` on ${formatDate(order.refunded_at)}` : ''}
              </p>
            )}
          </div>
        </div>

        {/* Lines. On a phone, qty and unit price fold under the description
            so the amount stays in view; paper always gets the full table. */}
        <div className="mt-8">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-navy text-2xs font-bold uppercase tracking-wider text-white">
                <th className="rounded-l-md px-3 py-2.5">Description</th>
                <th className="hidden w-12 px-2 py-2.5 text-right sm:table-cell print:table-cell">Qty</th>
                <th className="hidden w-28 px-2 py-2.5 text-right sm:table-cell print:table-cell">Unit price</th>
                <th className="w-24 rounded-r-md px-3 py-2.5 text-right sm:w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item) => {
                const options = specEntries(item.spec, optionGroups.data, ['remark'])
                const sizesOf = (matrix: Record<string, number>) =>
                  Object.entries(matrix)
                    .map(([size, qty]) => `${size} x ${qty}`)
                    .join(', ')
                // A line priced by size group (Kids 22-32) prints each price.
                const breakdown = item.price_breakdown && item.price_breakdown.length > 1 ? item.price_breakdown : null
                const sizes = breakdown
                  ? breakdown.map((row) => `${row.group ?? 'Standard'}: ${sizesOf(row.sizes)}`).join('  ·  ')
                  : sizesOf(item.size_matrix)
                return (
                  <tr key={item.id} className="break-inside-avoid border-b border-line align-top">
                    <td className="px-3 py-3.5">
                      <p className="font-semibold">{item.product}</p>
                      {options.length > 0 && (
                        <p className="mt-0.5 text-xs leading-relaxed text-muted">
                          {options.map((entry) => `${entry.label}: ${entry.value}`).join('  ·  ')}
                        </p>
                      )}
                      <p className="mt-0.5 text-xs leading-relaxed text-muted">
                        Sizes: {sizes}
                        {(item.personalizations?.length ?? 0) > 0 && `  ·  With names and numbers (${item.personalizations!.length})`}
                      </p>
                      {(item.urgent_surcharge ?? 0) > 0 && (
                        <p className="mt-0.5 text-xs">Includes urgent service +{money(item.urgent_surcharge!)}/pc</p>
                      )}
                      <p className="mt-1 text-xs font-semibold sm:hidden print:hidden">
                        {breakdown
                          ? breakdown.map((row) => (
                              <span key={row.group ?? ''} className="block">
                                {priceRowLabel({ group: row.group, qty: row.qty, price: row.unit_price })}
                              </span>
                            ))
                          : `${item.qty} pcs x ${money(item.unit_price)}`}
                      </p>
                    </td>
                    <td className="hidden px-2 py-3.5 text-right sm:table-cell print:table-cell">{item.qty}</td>
                    <td className="hidden px-2 py-3.5 text-right sm:table-cell print:table-cell">
                      {breakdown
                        ? breakdown.map((row) => (
                            <span key={row.group ?? ''} className="block whitespace-nowrap">
                              {row.group ?? 'Standard'} {money(row.unit_price)}
                            </span>
                          ))
                        : money(item.unit_price)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3.5 text-right font-semibold">{money(lineTotal(item))}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pay to and the note on the left, totals on the right */}
        <div className="mt-6 flex flex-col-reverse gap-6 sm:flex-row sm:items-start sm:justify-between print:flex-row print:items-start print:justify-between">
          <div className="min-w-0 max-w-sm space-y-4">
            {showBank && (
              <div>
                <Heading>Pay to</Heading>
                {seller.bank_name && <p>{seller.bank_name}</p>}
                {seller.bank_account_name && <p>{seller.bank_account_name}</p>}
                {seller.bank_account_no && <p className="font-mono font-bold">{seller.bank_account_no}</p>}
                <p className="mt-1 text-xs text-muted">Please use {order.number} as the payment reference.</p>
              </div>
            )}
            {seller.note && (
              <div>
                <Heading>Note</Heading>
                <p className="whitespace-pre-line text-xs leading-relaxed text-muted">{seller.note}</p>
              </div>
            )}
          </div>

          <div className="w-full shrink-0 sm:w-72 print:w-72">
            <p className="flex justify-between px-3 py-1.5">
              <span className="text-muted">Subtotal</span>
              <span>{money(order.subtotal ?? order.total)}</span>
            </p>
            <p className="flex justify-between px-3 py-1.5">
              <span className="text-muted">Shipping</span>
              <span>{money(order.delivery_fee ?? 0)}</span>
            </p>
            <p className="mt-2 flex items-baseline justify-between rounded-md bg-navy px-3 py-3 text-white">
              <span className="font-semibold">Total ({order.total_pieces} pcs)</span>
              <span className="text-lg font-extrabold">{money(order.total)}</span>
            </p>
          </div>
        </div>

        <div className="mt-10 border-t border-line pt-4 text-center">
          <p className="font-semibold text-navy">Thank you for your business.</p>
          <p className="mt-1 text-2xs text-muted">This is a computer-generated invoice. No signature is required.</p>
        </div>
      </div>
    </div>
  )
}
